import { useUser } from "@-/com.npmjs.expo-superwall";
import { Platform } from "@-/com.npmjs.react-native";
import { useServerPremium } from "#use-server-premium";

/**
 * Whether the viewer may read premium content.
 *
 * Premium when EITHER source says so:
 * - Superwall's device-local `subscriptionStatus` flips to `ACTIVE` the moment StoreKit confirms a
 *   purchase, while the paywall is still closing. That is what lets a gated screen unlock the
 *   instant the buyer returns to it. It is also the only source for a signed-out buyer, whose
 *   purchase exists solely as the store receipt on this device. Ignored on web: `expo-superwall`
 *   has no web SDK, so whatever our web stub reports is not a real entitlement.
 * - The server's entitlement verdict covers subscriptions this device's store account does not
 *   hold, such as one bought on another device under the same Haniverse account.
 *
 * The server's `false` deliberately does NOT override the device's `ACTIVE`: the server learns of
 * a purchase only from Superwall's webhook, which trails the device by seconds at best. Letting it
 * win kept buyers staring at the upgrade prompt they had just paid to remove.
 *
 * Gating UI on this is fine. Gating BYTES is not: premium mnemonic text is stripped from the
 * client database and only served by the authenticated worker route, which re-checks entitlement
 * server-side rather than trusting anything computed here.
 */
export function useIsPremium(): boolean {
  const { subscriptionStatus } = useUser();
  // Native only — the web stub's status is not a real entitlement.
  const isDeviceSubscribed = Platform.OS !== "web" && subscriptionStatus.status === "ACTIVE";
  const serverPremium = useServerPremium();

  return isDeviceSubscribed || serverPremium === true;
}
